import AssessmentIcon from '@mui/icons-material/Assessment';
import BarChartIcon from '@mui/icons-material/BarChart';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import PaymentsIcon from '@mui/icons-material/Payments';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ReportIcon from '@mui/icons-material/Report';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Logo, TAGLINE } from '@norvix/ui';
import { NavLink } from 'react-router-dom';

import { can, type Capability } from '../auth/capabilities.js';
import { useAuth } from '../auth/useAuth.js';

const DRAWER_WIDTH = 256;

interface NavItem {
  to: string;
  label: string;
  icon: JSX.Element;
  capability: Capability;
}

const NAV_ITEMS: readonly NavItem[] = [
  {
    to: '/dashboard',
    label: 'Dashboard',
    icon: <DashboardIcon />,
    capability: 'console',
  },
  {
    to: '/customers',
    label: 'Customers',
    icon: <PeopleIcon />,
    capability: 'customers.view',
  },
  {
    to: '/onboarding-applications',
    label: 'Onboarding',
    icon: <HowToRegIcon />,
    capability: 'kyc.decide',
  },
  {
    to: '/aml-alerts',
    label: 'AML Alerts',
    icon: <ReportIcon />,
    capability: 'aml.view',
  },
  {
    to: '/transactions',
    label: 'Transactions',
    icon: <PaymentsIcon />,
    capability: 'transactions.view',
  },
  {
    to: '/reconciliation',
    label: 'Reconciliation',
    icon: <ReceiptIcon />,
    capability: 'reconciliation.view',
  },
  {
    to: '/commissions',
    label: 'Fees & Commissions',
    icon: <BarChartIcon />,
    capability: 'fees.view',
  },
  {
    to: '/reports',
    label: 'Reports',
    icon: <AssessmentIcon />,
    capability: 'reports.financial',
  },
  {
    to: '/settings',
    label: 'System Settings',
    icon: <SettingsIcon />,
    capability: 'settings.view',
  },
  // Design System is intentionally absent from the navigation: it is a
  // reference for whoever is building the console, not something an operator
  // needs while working. Still reachable at /styleguide for that purpose.
];

export function Sidebar({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const { user } = useAuth();
  const compact = useMediaQuery(useTheme().breakpoints.down('md'));
  const role = user?.role;

  return (
    <Drawer
      variant={compact ? 'temporary' : 'permanent'}
      open={compact ? open : true}
      onClose={onClose}
      sx={{
        width: compact ? 0 : DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
        },
      }}
    >
      <Toolbar sx={{ px: 3 }}>
        <Logo height={24} />
      </Toolbar>
      <Box sx={{ px: 3, pb: 2 }}>
        <Typography variant="caption" sx={{ color: 'text.muted' }}>
          {TAGLINE}
        </Typography>
      </Box>
      <List sx={{ px: 1, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {NAV_ITEMS.filter((item) => can(role, item.capability)).map((item) => (
          <ListItemButton
            key={item.to}
            component={NavLink}
            to={item.to}
            onClick={compact ? onClose : undefined}
            sx={{
              flexGrow: 0,
              flexShrink: 0,
              minHeight: 44,
              mx: 1,
              ...(item.to === '/settings'
                ? { mt: 'auto', pt: 1.5, borderTop: 1, borderColor: 'divider' }
                : {}),
              mb: 0.5,
              borderRadius: 2,
              color: 'text.secondary',
              '& .MuiListItemIcon-root': { color: 'text.secondary', minWidth: 36 },
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.04)',
                color: 'text.primary',
              },
              '&.active': {
                backgroundColor: 'rgba(0, 201, 252, 0.12)',
                color: 'primary.light',
                '& .MuiListItemIcon-root': { color: 'primary.light' },
              },
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
            />
          </ListItemButton>
        ))}
      </List>
    </Drawer>
  );
}
